import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadFile } from "../lib/api";
import { Section, PageHeader, EmptyState, Avatar } from "../components/fields";
import FormFillSheet from "../components/FormFillSheet";
import { FileText, Download, PenLine, Search, Clock, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const EST_TIME = { daily_support_log: "~1 min", weekly_summary: "~5 min", behavior_concern: "~2 min", incident_report: "~4 min", communication_log: "~1 min" };

export default function FormsLibrary() {
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [fillForm, setFillForm] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/forms").then((r) => setForms(r.data));
    api.get("/form-submissions").then((r) => setSubmissions(r.data));
    api.get("/students").then((r) => setStudents(r.data));
  };
  useEffect(load, []);

  const openForm = (f) => {
    if (f.key === "daily_support_log") { navigate("/"); return; }
    if (f.key === "weekly_summary") { navigate("/weekly-review"); return; }
    setFillForm(f);
  };

  const downloadTemplate = async (f) => {
    try { await downloadFile(`/forms/${f.key}/pdf`, `supportpro_hub_${f.key}.pdf`); toast.success("Blank form downloaded"); }
    catch { toast.error("Download failed"); }
  };

  const studentName = (id) => {
    const s = students.find((x) => x.id === id);
    return s ? `${s.first_name} ${s.last_name}` : "—";
  };

  const lastUsed = (key) => {
    const sub = submissions.find((s) => s.form_key === key);
    return sub ? sub.date : null;
  };

  const filtered = forms.filter((f) => `${f.name} ${f.description}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-5 stagger">
      <PageHeader title="Forms" subtitle="TAG-designed form library. Field adjustments by request through TAG Admin." />

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
        <input data-testid="forms-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search forms..."
          className="w-full rounded-[10px] border border-[#E5E9EE] bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl">
          <EmptyState icon={ClipboardList} title="No forms match your search" hint="Try a different search term, or clear the search to see all forms." />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((f) => (
            <div key={f.id} data-testid={`form-card-${f.key}`} className="sf-card-hover bg-white border border-[#E5E9EE] rounded-2xl p-5 flex flex-col shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
              <div className="flex items-start justify-between">
                <span className="w-10 h-10 rounded-xl bg-[#EAF8F3] text-[#087F5B] flex items-center justify-center mb-3"><FileText size={18} /></span>
                <span className="flex items-center gap-1 text-[11px] text-[#667085] bg-[#F7F9FA] border border-[#E5E9EE] px-2 py-1 rounded-full"><Clock size={11} /> {EST_TIME[f.key] || "~2 min"}</span>
              </div>
              <h3 className="font-heading text-[15px] font-semibold text-[#132238]">{f.name}</h3>
              <p className="text-[13px] text-[#667085] mt-1 leading-relaxed flex-1">{f.description}</p>
              {lastUsed(f.key) && <p className="text-[11px] text-[#98A2B3] mt-2.5">Last used {lastUsed(f.key)}</p>}
              <div className="mt-4 flex gap-2">
                <button data-testid={`fill-form-btn-${f.key}`} onClick={() => openForm(f)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-3.5 py-2 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <PenLine size={13} /> {f.key === "daily_support_log" ? "Open Today" : f.key === "weekly_summary" ? "Open Weekly Review" : "Open Form"}
                </button>
                <button data-testid={`download-form-btn-${f.key}`} onClick={() => downloadTemplate(f)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#E5E9EE] text-[#132238] hover:bg-slate-50 px-3.5 py-2 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <Download size={13} /> PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Section title="Recent Submissions">
        {submissions.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No submissions yet" hint="Completed forms appear here for quick reference." />
        ) : (
          <div className="space-y-2">
            {submissions.map((s) => (
              <div key={s.id} className="border border-[#E5E9EE] rounded-xl p-3.5 flex items-center gap-3 flex-wrap">
                <Avatar name={s.aide_name} size="sm" tone="mint" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#132238]">{forms.find((f) => f.key === s.form_key)?.name || s.form_key}</p>
                  <p className="text-xs text-[#667085]">{studentName(s.student_id)} · {s.date} · by {s.aide_name}</p>
                </div>
                {s.data?.description && <p className="text-xs text-[#667085] max-w-md truncate">{s.data.description}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>
      {fillForm && <FormFillSheet form={fillForm} onClose={() => setFillForm(null)} onSaved={() => { setFillForm(null); load(); }} />}
    </div>
  );
}
